import { GameObjects, Geom, Math as PhaserMath, Scene } from 'phaser';
import { applyBalloonOutcome, createInitialProgress, type GameProgress } from '../domain/gameRules';
import {
  beginTrainQuestionResult,
  finishTrainQuestionResult,
  registerTrainWrongHit,
  type ActiveTrainQuestionResult,
} from '../domain/trainQuestionResults';
import { calculateTrainLayout } from '../domain/trainLayout';
import { buildLearningReview } from '../domain/learningReview';
import { getBalloonVisualMetrics, getWagonQuestionFontSize } from '../domain/textLayout';
import { createLearningResultOverlay } from '../ui/LearningResultOverlay';
import {
  DEMO_LEVELS,
  beginNextLevel,
  completeActiveQuestion,
  createInitialLevelProgress,
  getActiveQuestion,
  getWagonRenderStates,
  type LevelProgress,
  type TrainLevel,
  type TrainQuestion,
  type WagonStatus,
} from '../domain/levelProgression';
import {
  EventBus,
  TRAIN_GAME_FINISHED_EVENT,
  TRAIN_HUD_UPDATE_EVENT,
  type TrainGameResult,
  type TrainHudUpdate,
} from '../EventBus';
import type { TrainQuestionResult } from '../../../shared/results';

type BalloonChoice = { id: number; label: string; correct: boolean; mediaUrl?: string };
type BalloonActor = {
  root: GameObjects.Container;
  choice: BalloonChoice;
  vx: number;
  vy: number;
  falling: boolean;
  resolved: boolean;
  width: number;
  height: number;
  lane: number;
};

export type TrainAdventureSnapshot = {
  phase: 'playing' | 'transitioning' | 'won' | 'lost';
  level: number;
  levelQuestionCount: number;
  activeQuestionNumber: number;
  activeQuestion: string;
  expectedAnswer: string;
  score: number;
  lives: number;
  streak: number;
  trainOffsetX: number;
  wagons: Array<{ questionNumber: number; prompt: string; status: WagonStatus }>;
  balloons: Array<{ label: string; x: number; y: number; falling: boolean }>;
};

export type TrainHudState = {
  level: number;
  totalLevels: number;
  instruction: string;
  score: number;
  lives: number;
};

const WORLD_WIDTH = 1280;
const WORLD_HEIGHT = 720;
const TRAIN_BASELINE = 585;
const PLAYFIELD_TOP_GAP = 16;
const BALLOON_LANE_GAP = 154;
const GRAVITY = 580;
const SPAWN_INTERVAL = 1350;
const WAGON_GAP = 10;
const LOCOMOTIVE_WIDTH = 270;
const ACTIVE_WAGON_CENTER_X = 750;
const IMAGE_ONLY_PROMPT_MAX_HEIGHT = 190;
const IMAGE_ONLY_PROMPT_Y = 448;

type WagonStyle = 'wide' | 'narrow';

const WAGON_STYLE_METRICS = {
  wide: {
    width: 450,
    redInset: 6,
    redHeight: 162,
    plateInset: 24,
    plateHeight: 136,
    promptMediaMax: 180,
    promptGap: 2,
    promptBoxHeight: 96,
    promptY: 456,
    labelY: 518,
    maxLines: 4,
    minFontSize: 19,
    dropTop: 395,
    dropHeight: 156,
    wheelInset: 115,
  },
  narrow: {
    width: 286,
    redInset: 12,
    redHeight: 145,
    plateInset: 38,
    plateHeight: 104,
    promptMediaMax: 0,
    promptGap: 0,
    promptBoxHeight: 70,
    promptY: 454,
    labelY: 501,
    maxLines: 3,
    minFontSize: 18,
    dropTop: 405,
    dropHeight: 143,
    wheelInset: 74,
  },
} as const satisfies Record<WagonStyle, Record<string, number>>;

function resolveWagonStyle(question: TrainQuestion): WagonStyle {
  if (question.promptMediaUrl) return 'wide';
  return question.wagonStyle ?? 'wide';
}

export class TrainAdventureScene extends Scene {
  private readonly levels: TrainLevel[];
  private readonly gameId: string;
  private readonly restartEnabled: boolean;
  private readonly mediaTextureKeys = new Map<string, string>();
  private progress: GameProgress = createInitialProgress();
  private levelProgress: LevelProgress = createInitialLevelProgress();
  private balloons: BalloonActor[] = [];
  private spawnElapsed = 600;
  private nextChoice = 0;
  private isTransitioning = false;
  private feedbackText!: GameObjects.Text;
  private wagonDropZone = new Geom.Rectangle(0, 0, 0, 0);
  private train?: GameObjects.Container;
  private trainShadow?: GameObjects.Ellipse;
  private steamOriginX = 1035;
  private resultEmitted = false;
  private questionResults: TrainQuestionResult[] = [];
  private activeQuestionResult?: ActiveTrainQuestionResult;

  constructor(levels: TrainLevel[] = DEMO_LEVELS, gameId = 'train-game', restartEnabled = true) {
    super('TrainAdventure');
    this.levels = levels;
    this.gameId = gameId;
    this.restartEnabled = restartEnabled;
  }

  preload() {
    // Yayın bağlantısı /odevlendir/oyna/<slug> altında açıldığından göreli yol
    // kullanılamaz; aksi halde tarayıcı görseli /oyna/assets altında arar.
    this.load.image('countryside-background', '/odevlendir/assets/train-adventure/countryside-background.png');
    const mediaUrls = new Set<string>();
    for (const level of this.levels) {
      for (const question of level.questions) {
        if (question.promptMediaUrl) mediaUrls.add(question.promptMediaUrl);
        for (const choice of question.choices) if (choice.mediaUrl) mediaUrls.add(choice.mediaUrl);
      }
    }
    [...mediaUrls].forEach((mediaUrl, index) => {
      const textureKey = `${this.gameId}-media-${index}`;
      this.mediaTextureKeys.set(mediaUrl, textureKey);
      this.load.image(textureKey, mediaUrl);
    });
  }

  create() {
    this.progress = createInitialProgress();
    this.levelProgress = createInitialLevelProgress();
    this.balloons = [];
    this.spawnElapsed = 600;
    this.nextChoice = 0;
    this.isTransitioning = false;
    this.resultEmitted = false;
    this.questionResults = [];
    this.activeQuestionResult = undefined;

    this.createWorld();
    this.createTrain();
    this.createFeedback();
    this.createSteamLoop();
    this.beginActiveQuestionResult();
    this.installDebugHooks();
    this.showLevelBanner();
    EventBus.emit('current-scene-ready', this);
  }

  update(_time: number, delta: number) {
    this.simulate(Math.min(delta, 40));
  }

  private get activeQuestion(): TrainQuestion {
    return getActiveQuestion(this.levelProgress, this.levels);
  }

  private createWorld() {
    this.add.image(WORLD_WIDTH / 2, WORLD_HEIGHT / 2, 'countryside-background')
      .setDisplaySize(WORLD_WIDTH, WORLD_HEIGHT);
    const shade = this.add.graphics();
    shade.fillGradientStyle(0x10282d, 0x10282d, 0x10282d, 0x10282d, 0.18, 0.04, 0, 0.24);
    shade.fillRect(0, 0, WORLD_WIDTH, WORLD_HEIGHT);
  }

  private createTrain() {
    if (this.train) {
      this.tweens.killTweensOf(this.train.list);
      this.tweens.killTweensOf(this.train);
      this.train.destroy(true);
    }
    this.trainShadow?.destroy();
    const wagonStates = getWagonRenderStates(this.levelProgress, this.levels);
    const layout = this.getCurrentTrainLayout();
    const train = this.add.container(layout.forwardOffset, 0).setDepth(10);

    this.trainShadow = this.add.ellipse(
      660,
      TRAIN_BASELINE + 5,
      1190,
      34,
      0x0b1918,
      0.38,
    ).setDepth(9);

    const visualWagons = [...wagonStates].reverse();
    visualWagons.forEach((wagonState) => {
      const wagonLayout = layout.wagons.find((wagon) => wagon.questionIndex === wagonState.questionIndex)!;
      const centerX = wagonLayout.baseCenterX;
      const style = resolveWagonStyle(wagonState.question);
      const metrics = WAGON_STYLE_METRICS[style];
      train.add(this.createWagon(
        centerX,
        wagonLayout.width,
        wagonState.question,
        wagonState.questionIndex + 1,
        wagonState.status,
      ));
      if (wagonState.status === 'active') {
        this.wagonDropZone.setTo(
          wagonLayout.centerX - wagonLayout.width * 0.41,
          metrics.dropTop,
          wagonLayout.width * 0.82,
          metrics.dropHeight,
        );
      }
    });

    this.steamOriginX = layout.locomotiveCenterX + 66;
    train.add(this.createLocomotive(layout.locomotiveBaseCenterX));
    this.train = train;

    this.tweens.add({
      targets: train,
      y: { from: -1.5, to: 1.5 },
      duration: 550,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.InOut',
    });
  }

  private getCurrentTrainLayout() {
    return calculateTrainLayout({
      worldWidth: WORLD_WIDTH,
      locomotiveWidth: LOCOMOTIVE_WIDTH,
      wagonGap: WAGON_GAP,
      wagonWidths: this.levels[this.levelProgress.levelIndex].questions.map(
        (question) => WAGON_STYLE_METRICS[resolveWagonStyle(question)].width,
      ),
      activeWagonCenterX: ACTIVE_WAGON_CENTER_X,
      completedCount: this.levelProgress.questionIndex,
    });
  }

  private createWagon(
    centerX: number,
    width: number,
    question: TrainQuestion,
    questionNumber: number,
    status: WagonStatus,
  ) {
    const isActive = status === 'active';
    const isCompleted = status === 'completed';
    const isImageOnly = Boolean(question.promptMediaUrl && !question.prompt.trim());
    const style = resolveWagonStyle(question);
    const metrics = WAGON_STYLE_METRICS[style];
    const wagon = this.add.container(0, 0);
    const left = centerX - width / 2;
    const top = 382;
    const outer = this.add.graphics();
    outer.fillStyle(0x2f2020, 1);
    outer.fillRoundedRect(left, top, width, 174, Math.min(22, width * 0.1));
    outer.lineStyle(
      isActive ? 8 : 5,
      isActive ? 0xffdc71 : isCompleted ? 0x7fc58a : 0xb58a45,
      1,
    );
    outer.strokeRoundedRect(left, top, width, 174, Math.min(22, width * 0.1));
    outer.fillGradientStyle(0x7e2d2e, 0x9b3a35, 0x5b2025, 0x69262a, 1);
    outer.fillRoundedRect(
      left + metrics.redInset,
      top + metrics.redInset,
      width - metrics.redInset * 2,
      metrics.redHeight,
      style === 'wide' ? 14 : 12,
    );

    const plateWidth = width - metrics.plateInset;
    const questionPlate = this.add.rectangle(
      centerX,
      465,
      plateWidth,
      metrics.plateHeight,
      isActive ? 0xf8e39f : isCompleted ? 0xb9d6ac : 0xd7c995,
      0.98,
    ).setStrokeStyle(isActive ? 5 : 3, isActive ? 0x5b3528 : isCompleted ? 0x416e4d : 0x705a3c);
    const promptLayout = this.calculatePromptLayout(question, plateWidth, metrics);
    const promptMediaWidth = promptLayout.mediaWidth;
    const promptMediaHeight = promptLayout.mediaHeight;
    const promptGap = promptMediaWidth > 0 ? metrics.promptGap : 0;
    const promptBoxWidth = promptLayout.textWidth;
    const promptBoxHeight = metrics.promptBoxHeight;
    const contentLeft = centerX - plateWidth / 2 + 9;
    const promptCenterX = contentLeft + promptMediaWidth + promptGap + promptBoxWidth / 2;
    const promptSize = promptLayout.fontSize;
    const prompt = this.add.text(promptCenterX, metrics.promptY, question.prompt, {
      fontFamily: 'Georgia, serif', fontSize: `${promptSize}px`, color: '#40251e', fontStyle: 'bold',
      align: 'center', lineSpacing: -3,
      wordWrap: { width: promptBoxWidth, useAdvancedWrap: true },
    }).setOrigin(0.5).setMaxLines(metrics.maxLines);
    this.fitTextToBox(prompt, promptBoxWidth, promptBoxHeight, promptSize, metrics.minFontSize);
    const promptMedia = question.promptMediaUrl
      ? this.createFittedImage(
          question.prompt.trim() ? contentLeft + promptMediaWidth / 2 : centerX,
          isImageOnly ? IMAGE_ONLY_PROMPT_Y : metrics.promptY,
          question.promptMediaUrl,
          promptMediaWidth,
          promptMediaHeight,
        )
      : undefined;
    const statusLabel = isActive ? 'AKTİF VAGON' : isCompleted ? 'TAMAMLANDI ✓' : `SIRADA ${questionNumber}`;
    const label = this.add.text(centerX, metrics.labelY, statusLabel, {
      fontFamily: 'Inter, sans-serif', fontSize: '12px',
      color: isCompleted ? '#315e3e' : '#765040', fontStyle: 'bold',
    }).setOrigin(0.5);
    const badge = this.add.circle(
      centerX,
      375,
      23,
      isActive ? 0xf7c95f : isCompleted ? 0x4f8b61 : 0x17383f,
    )
      .setStrokeStyle(3, 0xffe9a5);
    const badgeText = this.add.text(centerX, 375, String(questionNumber), {
      fontFamily: 'Inter, sans-serif', fontSize: '20px',
      color: isActive ? '#223a3d' : '#fff4c9', fontStyle: 'bold',
    }).setOrigin(0.5);

    const wheelRadius = PhaserMath.Clamp(width * 0.13, 20, 34);
    const wheelInset = metrics.wheelInset;
    const wheels = [
      this.createWheel(centerX - wheelInset, TRAIN_BASELINE - 17, wheelRadius),
      this.createWheel(centerX + wheelInset, TRAIN_BASELINE - 17, wheelRadius),
    ];
    const contentObjects = isImageOnly
      ? [prompt, label, ...(promptMedia ? [promptMedia] : [])]
      : [...(promptMedia ? [promptMedia] : []), prompt, label];
    wagon.add([outer, questionPlate, ...contentObjects, badge, badgeText, ...wheels]);
    return wagon;
  }

  private createLocomotive(centerX: number) {
    const locomotive = this.add.container(0, 0);
    const left = centerX - LOCOMOTIVE_WIDTH / 2;
    const body = this.add.graphics();
    body.fillGradientStyle(0x1f5960, 0x347783, 0x103d45, 0x174d56, 1);
    body.fillRoundedRect(left + 8, 432, 245, 117, 23);
    body.fillRoundedRect(left + 38, 372, 105, 111, 14);
    body.fillStyle(0x152a2d, 1);
    body.fillRoundedRect(left + 178, 389, 48, 91, 9);
    body.fillStyle(0xd6aa45, 1);
    body.fillRect(left + 11, 530, 245, 17);
    body.fillStyle(0x102a31, 1);
    body.fillRect(left + 58, 387, 66, 58);
    body.fillStyle(0x9ed5da, 0.65);
    body.fillRect(left + 66, 395, 50, 42);
    body.fillStyle(0x223236, 1);
    body.fillRect(left + 186, 352, 31, 48);
    body.fillEllipse(left + 201, 351, 66, 16);
    body.fillStyle(0xd7a944, 1);
    body.fillCircle(left + 247, 457, 17);
    body.fillStyle(0xf8df92, 1);
    body.fillCircle(left + 251, 453, 7);

    const namePlate = this.add.text(left + 126, 481, 'ÖĞRENME EKSPRESİ', {
      fontFamily: 'Georgia, serif', fontSize: '14px', color: '#f7dfa1', fontStyle: 'bold',
    }).setOrigin(0.5);
    const wheels = [
      this.createWheel(left + 58, TRAIN_BASELINE - 17, 34),
      this.createWheel(left + 136, TRAIN_BASELINE - 17, 34),
      this.createWheel(left + 215, TRAIN_BASELINE - 21, 42),
    ];
    const cowcatcher = this.add.triangle(0, 0, left + 254, 543, left + 290, 571, left + 252, 571, 0xd1a447);
    locomotive.add([body, namePlate, ...wheels, cowcatcher]);
    return locomotive;
  }

  private createWheel(x: number, y: number, radius: number) {
    const wheel = this.add.container(x, y);
    const tyre = this.add.circle(0, 0, radius, 0x1a2324).setStrokeStyle(Math.max(3, radius * 0.12), 0xc5953d);
    const hub = this.add.circle(0, 0, radius * 0.3, 0xd3a84c).setStrokeStyle(2, 0x5b401f);
    const spokes = this.add.graphics();
    spokes.lineStyle(Math.max(2, radius * 0.07), 0xb68535, 0.95);
    for (let angle = 0; angle < Math.PI * 2; angle += Math.PI / 4) {
      spokes.lineBetween(0, 0, Math.cos(angle) * radius * 0.72, Math.sin(angle) * radius * 0.72);
    }
    wheel.add([tyre, spokes, hub]);
    this.tweens.add({ targets: wheel, angle: 360, duration: 1700, repeat: -1 });
    return wheel;
  }

  private createFeedback() {
    this.feedbackText = this.add.text(WORLD_WIDTH / 2, 650, '', {
      fontFamily: 'Inter, sans-serif', fontSize: '24px', color: '#fff2bc', fontStyle: 'bold',
      backgroundColor: '#10282de8', padding: { x: 18, y: 10 },
    }).setOrigin(0.5).setDepth(60).setAlpha(0);
    this.refreshHud();
  }

  private refreshHud() {
    const level = this.levels[this.levelProgress.levelIndex];
    const update: TrainHudUpdate = {
      gameId: this.gameId,
      hud: {
        level: level.id,
        totalLevels: this.levels.length,
        instruction: `Önce ${this.levelProgress.questionIndex + 1}. vagonu cevapla • ${level.questions.length} vagondan ${this.levelProgress.questionIndex + 1}. soru`,
        score: this.progress.score,
        lives: this.progress.lives,
      },
    };
    EventBus.emit(TRAIN_HUD_UPDATE_EVENT, update);
  }

  private createSteamLoop() {
    this.time.addEvent({
      delay: 680,
      loop: true,
      callback: () => {
        if (this.levelProgress.phase === 'lost' || this.levelProgress.phase === 'won') return;
        const puff = this.add.circle(this.steamOriginX, 330, PhaserMath.Between(10, 18), 0xf2eee4, 0.58).setDepth(8);
        this.tweens.add({
          targets: puff,
          x: puff.x - PhaserMath.Between(25, 55), y: puff.y - PhaserMath.Between(55, 88),
          alpha: 0, scale: 1.8, duration: 1900, onComplete: () => puff.destroy(),
        });
      },
    });
  }

  private simulate(delta: number) {
    if (this.levelProgress.phase !== 'playing' || this.isTransitioning) return;
    this.spawnElapsed += delta;
    const choices = this.getChoices();
    if (this.spawnElapsed >= SPAWN_INTERVAL && this.balloons.length < choices.length) {
      if (this.spawnBalloon(choices[this.nextChoice])) {
        this.spawnElapsed = 0;
        this.nextChoice = (this.nextChoice + 1) % choices.length;
      } else {
        this.spawnElapsed = SPAWN_INTERVAL - 140;
      }
    }

    for (const actor of [...this.balloons]) {
      if (actor.resolved) continue;
      const seconds = delta / 1000;
      actor.root.x += actor.vx * seconds;
      if (actor.falling) {
        actor.vy += GRAVITY * seconds;
        actor.root.y += actor.vy * seconds;
      } else {
        actor.root.y += Math.sin((this.time.now + actor.choice.id * 400) / 430) * 0.22;
      }
      const bounds = new Geom.Rectangle(
        actor.root.x - actor.width / 2,
        actor.root.y - actor.height / 2,
        actor.width,
        actor.height,
      );
      if (actor.falling && Geom.Intersects.RectangleToRectangle(bounds, this.wagonDropZone)) {
        this.resolveHit(actor);
      } else if (actor.root.y > WORLD_HEIGHT + actor.height || (!actor.falling && actor.root.x < -actor.width / 2 - 24)) {
        this.recycleBalloon(actor);
      }
    }
  }

  private getChoices(): BalloonChoice[] {
    return this.activeQuestion.choices.map((choice, id) => ({
      id,
      label: choice.label,
      correct: choice.correct,
      mediaUrl: choice.mediaUrl,
    }));
  }

  private spawnBalloon(choice: BalloonChoice): boolean {
    const palette = [0xd5524a, 0x347db5, 0xe1a53d, 0x8055a8];
    const balloonColor = palette[choice.id % palette.length];
    const hasLabel = choice.label.trim().length > 0;
    const metrics = getBalloonVisualMetrics(choice.label, Boolean(choice.mediaUrl));
    const occupiedWidth = Math.max(metrics.width, metrics.mediaWidth);
    const bodyTop = PLAYFIELD_TOP_GAP;
    const spawnX = WORLD_WIDTH + occupiedWidth / 2 + 24;
    const preferredLane = choice.id % 2;
    const lane = [preferredLane, 1 - preferredLane].find((candidate) =>
      this.canSpawnInLane(candidate, spawnX, occupiedWidth),
    );
    if (lane === undefined) return false;
    const laneY = bodyTop + metrics.height / 2 + lane * BALLOON_LANE_GAP;
    const root = this.add.container(spawnX, laneY).setDepth(35);
    const shadow = this.add.ellipse(5, 8, metrics.width, metrics.height, 0x10282d, 0.2);
    const body = this.add.ellipse(0, 0, metrics.width, metrics.height, balloonColor).setStrokeStyle(5, 0xfff1ce, 0.78);
    const glow = this.add.ellipse(
      -metrics.width * 0.2,
      -metrics.height * 0.2,
      Math.min(30, metrics.width * 0.24),
      Math.min(40, metrics.height * 0.32),
      0xffffff,
      0.35,
    ).setAngle(28);
    const balloonBottom = metrics.height / 2;
    const knot = this.add.triangle(0, 0, -9, balloonBottom, 9, balloonBottom, 0, balloonBottom + 14, balloonColor);
    const string = this.add.graphics();
    string.lineStyle(2, 0xf4ead2, 0.82);
    string.lineBetween(0, balloonBottom + 14, 5, balloonBottom + 48);
    const textBoxWidth = hasLabel ? metrics.width - 28 - metrics.mediaWidth - metrics.mediaGap : 0;
    const labelX = choice.mediaUrl ? metrics.mediaWidth / 2 + metrics.mediaGap / 2 : 0;
    const label = hasLabel
      ? this.add.text(labelX, 0, choice.label, {
          fontFamily: 'Georgia, serif', fontSize: `${metrics.fontSize}px`, color: '#fff9e8',
          fontStyle: 'bold', stroke: '#2e2523', strokeThickness: metrics.fontSize <= 20 ? 3 : 4,
          align: 'center', lineSpacing: -2,
          wordWrap: { width: textBoxWidth, useAdvancedWrap: true },
        }).setOrigin(0.5).setMaxLines(metrics.maxLines)
      : undefined;
    if (label) this.fitTextToBox(label, textBoxWidth, metrics.height - 28, metrics.fontSize, 16);
    const choiceMedia = choice.mediaUrl
      ? this.createFittedImage(
          hasLabel ? -metrics.width / 2 + 14 + metrics.mediaWidth / 2 : 0,
          0,
          choice.mediaUrl,
          metrics.mediaWidth,
          metrics.mediaHeight,
        )
      : undefined;
    root.add([shadow, body, glow, knot, string, ...(choiceMedia ? [choiceMedia] : []), ...(label ? [label] : [])]);
    root.setSize(occupiedWidth + 12, metrics.height + 56).setInteractive({ useHandCursor: true });
    const actor: BalloonActor = {
      root,
      choice,
      vx: -118 - choice.id * 5,
      vy: 0,
      falling: false,
      resolved: false,
      width: occupiedWidth,
      height: metrics.height,
      lane,
    };
    root.on('pointerdown', () => {
      if (actor.falling || actor.resolved || this.isTransitioning) return;
      actor.falling = true;
      actor.vy = 42;
      this.tweens.add({ targets: root, angle: 8, duration: 190, yoyo: true, repeat: 1 });
    });
    this.balloons.push(actor);
    return true;
  }

  private canSpawnInLane(lane: number, spawnX: number, width: number): boolean {
    const newLeft = spawnX - width / 2;
    return this.balloons
      .filter((actor) => !actor.resolved && !actor.falling && actor.lane === lane)
      .every((actor) => actor.root.x + actor.width / 2 + 24 < newLeft);
  }

  private calculatePromptLayout(
    question: TrainQuestion,
    plateWidth: number,
    metrics: (typeof WAGON_STYLE_METRICS)[WagonStyle],
  ) {
    const availableWidth = plateWidth - 18;
    if (!question.promptMediaUrl) {
      return {
        mediaWidth: 0,
        mediaHeight: 0,
        textWidth: availableWidth,
        fontSize: getWagonQuestionFontSize(question.prompt, availableWidth),
      };
    }

    if (!question.prompt.trim()) {
      const media = this.getFittedMediaDimensions(
        question.promptMediaUrl,
        availableWidth,
        IMAGE_ONLY_PROMPT_MAX_HEIGHT,
      );
      return {
        mediaWidth: media.width,
        mediaHeight: media.height,
        textWidth: Math.max(1, availableWidth - media.width),
        fontSize: metrics.minFontSize,
      };
    }

    const minimumMediaWidth = 72;
    for (let requestedWidth = metrics.promptMediaMax; requestedWidth >= minimumMediaWidth; requestedWidth -= 12) {
      const media = this.getFittedMediaDimensions(
        question.promptMediaUrl,
        requestedWidth,
        metrics.promptBoxHeight + 16,
      );
      const textWidth = availableWidth - media.width - metrics.promptGap;
      const preferredFontSize = getWagonQuestionFontSize(question.prompt, textWidth);
      if (this.doesPromptFit(question.prompt, textWidth, metrics.promptBoxHeight, preferredFontSize, metrics)) {
        return { mediaWidth: media.width, mediaHeight: media.height, textWidth, fontSize: preferredFontSize };
      }
    }

    const media = this.getFittedMediaDimensions(
      question.promptMediaUrl,
      minimumMediaWidth,
      metrics.promptBoxHeight + 16,
    );
    const textWidth = availableWidth - media.width - metrics.promptGap;
    return {
      mediaWidth: media.width,
      mediaHeight: media.height,
      textWidth,
      fontSize: getWagonQuestionFontSize(question.prompt, textWidth),
    };
  }

  private doesPromptFit(
    prompt: string,
    width: number,
    height: number,
    fontSize: number,
    metrics: (typeof WAGON_STYLE_METRICS)[WagonStyle],
  ): boolean {
    const probe = this.add.text(-2000, -2000, prompt, {
      fontFamily: 'Georgia, serif',
      fontSize: `${fontSize}px`,
      fontStyle: 'bold',
      lineSpacing: -3,
      wordWrap: { width, useAdvancedWrap: true },
    }).setVisible(false);
    const lineCount = probe.getWrappedText(prompt).length;
    const fits = lineCount <= metrics.maxLines && probe.height <= height;
    probe.destroy();
    return fits;
  }

  private getFittedMediaDimensions(mediaUrl: string, maxWidth: number, maxHeight: number) {
    const textureKey = this.mediaTextureKeys.get(mediaUrl);
    const frame = textureKey && this.textures.exists(textureKey) ? this.textures.getFrame(textureKey) : undefined;
    const sourceWidth = frame?.realWidth ?? maxWidth;
    const sourceHeight = frame?.realHeight ?? maxHeight;
    const scale = Math.min(maxWidth / Math.max(1, sourceWidth), maxHeight / Math.max(1, sourceHeight));
    return { width: sourceWidth * scale, height: sourceHeight * scale };
  }

  private fitTextToBox(
    text: GameObjects.Text,
    maxWidth: number,
    maxHeight: number,
    preferredFontSize: number,
    minimumFontSize: number,
  ) {
    text.setScale(1);
    for (let fontSize = preferredFontSize; fontSize >= minimumFontSize; fontSize -= 1) {
      text.setFontSize(fontSize);
      if (text.width <= maxWidth && text.height <= maxHeight) return;
    }

    const safeScale = Math.min(1, maxWidth / Math.max(1, text.width), maxHeight / Math.max(1, text.height));
    text.setScale(safeScale);
  }

  private createFittedImage(x: number, y: number, mediaUrl: string, maxWidth: number, maxHeight: number) {
    const textureKey = this.mediaTextureKeys.get(mediaUrl);
    if (!textureKey || !this.textures.exists(textureKey)) return undefined;
    const image = this.add.image(x, y, textureKey);
    const scale = Math.min(maxWidth / Math.max(1, image.width), maxHeight / Math.max(1, image.height));
    image.setScale(scale).setOrigin(0.5);
    return image;
  }

  private resolveHit(actor: BalloonActor) {
    actor.resolved = true;
    this.progress = applyBalloonOutcome(this.progress, actor.choice.correct ? 'correct-hit' : 'wrong-hit');
    this.refreshHud();
    if (actor.choice.correct) {
      this.completeActiveQuestionResult(true);
      const gained = 100 + Math.min(4, this.progress.streak - 1) * 10;
      this.showFeedback(`Doğru isabet! +${gained}`, '#fff2bc');
      this.beginQuestionTransition(actor);
    } else {
      if (this.activeQuestionResult) this.activeQuestionResult = registerTrainWrongHit(this.activeQuestionResult, actor.choice.id);
      this.showFeedback('Yanlış vagon! 1 can ve 25 puan kaybettin.', '#ffb0a6');
      if (this.progress.lives === 0) {
        this.recycleBalloon(actor);
        this.completeActiveQuestionResult(false);
        this.endGame();
        return;
      }
      this.tweens.add({
        targets: actor.root, x: '+=18', duration: 65, yoyo: true, repeat: 4,
        onComplete: () => this.recycleBalloon(actor),
      });
    }
  }

  private beginQuestionTransition(correctBalloon: BalloonActor) {
    this.isTransitioning = true;
    this.clearBalloons(correctBalloon);
    this.tweens.add({
      targets: correctBalloon.root,
      scale: 0.72,
      alpha: 0,
      duration: 330,
      onComplete: () => this.recycleBalloon(correctBalloon),
    });
    const previousLevel = this.levelProgress.levelIndex;
    this.levelProgress = completeActiveQuestion(this.levelProgress, this.levels);
    const nextTrainX = this.levelProgress.phase === 'playing'
      ? this.getCurrentTrainLayout().forwardOffset
      : (this.train?.x ?? 0) + 135;
    this.tweens.add({
      targets: this.train,
      x: nextTrainX,
      duration: 720,
      ease: 'Sine.InOut',
      onComplete: () => {
        if (this.levelProgress.phase === 'won') {
          this.showResultScreen(true);
          return;
        }
        if (this.levelProgress.phase === 'level-complete') {
          this.levelProgress = beginNextLevel(this.levelProgress);
        }
        const changedLevel = previousLevel !== this.levelProgress.levelIndex;
        this.nextChoice = 0;
        this.spawnElapsed = 600;
        this.createTrain();
        this.beginActiveQuestionResult();
        this.refreshHud();
        this.isTransitioning = false;
        if (changedLevel) this.showLevelBanner();
      },
    });
  }

  private showLevelBanner() {
    const levelNumber = this.levels[this.levelProgress.levelIndex].id;
    const banner = this.add.container(WORLD_WIDTH / 2, WORLD_HEIGHT / 2).setDepth(100);
    const panel = this.add.rectangle(0, 0, 390, 150, 0x0d2b31, 0.94).setStrokeStyle(4, 0xf0c75f);
    const title = this.add.text(0, -24, `SEVİYE ${levelNumber}`, {
      fontFamily: 'Georgia, serif', fontSize: '48px', color: '#ffe18b', fontStyle: 'bold',
    }).setOrigin(0.5);
    const count = this.levels[this.levelProgress.levelIndex].questions.length;
    const subtitle = this.add.text(0, 35, `${count} vagon • Soruları sırayla tamamla`, {
      fontFamily: 'Inter, sans-serif', fontSize: '18px', color: '#eaf6f2',
    }).setOrigin(0.5);
    banner.add([panel, title, subtitle]).setScale(0.8).setAlpha(0);
    this.tweens.add({
      targets: banner, alpha: 1, scale: 1, duration: 260, hold: 650, yoyo: true,
      onComplete: () => banner.destroy(),
    });
  }

  private endGame() {
    this.levelProgress = { ...this.levelProgress, phase: 'lost' };
    this.isTransitioning = true;
    this.clearBalloons();
    this.showResultScreen(false);
  }

  private beginActiveQuestionResult() {
    const level = this.levels[this.levelProgress.levelIndex];
    this.activeQuestionResult = beginTrainQuestionResult(
      level.id,
      this.levelProgress.questionIndex + 1,
      this.time.now,
      this.activeQuestion.choices.findIndex((choice) => choice.correct),
    );
  }

  private completeActiveQuestionResult(completed: boolean) {
    if (!this.activeQuestionResult) return;
    this.questionResults.push(finishTrainQuestionResult(this.activeQuestionResult, this.time.now, completed));
    this.activeQuestionResult = undefined;
  }

  private showResultScreen(won: boolean) {
    this.levelProgress = { ...this.levelProgress, phase: won ? 'won' : 'lost' };
    this.isTransitioning = true;
    this.clearBalloons();
    if (!this.resultEmitted) {
      const event: TrainGameResult = {
        gameId: this.gameId,
        result: {
          score: this.progress.score,
          lives: this.progress.lives,
          correct: this.progress.correct,
          wrong: this.progress.wrong,
          attempts: this.progress.attempts,
          completed: won,
          details: { templateKey: 'train-adventure', questions: this.questionResults },
        },
      };
      EventBus.emit(TRAIN_GAME_FINISHED_EVENT, event);
      this.resultEmitted = true;
    }
    const totalQuestionCount = this.levels.reduce((total, level) => total + level.questions.length, 0);
    const flatQuestions = this.levels.flatMap((level) => level.questions);
    const reviewQuestions = flatQuestions.map((question) => ({
      prompt: question.prompt,
      promptMediaUrl: question.promptMediaUrl,
      choices: question.choices.map((choice) => ({ text: choice.label, mediaUrl: choice.mediaUrl, correct: choice.correct })),
    }));
    const reviewItems = buildLearningReview(reviewQuestions, this.questionResults.map((result, index) => ({
      label: `Seviye ${result.level} · ${result.question}. vagon`,
      questionIndex: index,
      wrongChoiceIndices: result.wrongChoiceIndices,
      wrongCount: result.wrongHitCount,
      completed: result.completed,
      completionStatus: result.completed ? 'completed-after-mistake' : 'incomplete',
    })));
    const overlay = createLearningResultOverlay(this, {
      centerX: WORLD_WIDTH / 2,
      centerY: WORLD_HEIGHT / 2,
      viewportWidth: WORLD_WIDTH,
      viewportHeight: WORLD_HEIGHT,
      title: won ? 'YOLCULUK TAMAMLANDI!' : 'CANLARIN BİTTİ',
      titleColor: won ? '#ffe080' : '#ff9e92',
      accentColor: won ? 0xf2cb68 : 0xe47e70,
      summaryLines: [
        `${this.progress.score} puan · ${this.progress.correct}/${totalQuestionCount} doğru · ${this.progress.wrong} yanlış`,
        `${this.levels.length} seviye · ${totalQuestionCount} vagon`,
      ],
      reviewItems,
      restartEnabled: this.restartEnabled,
      restartLabel: 'Yeniden oyna',
      onRestart: () => this.scene.restart(),
      resolveTextureKey: (mediaUrl) => this.mediaTextureKeys.get(mediaUrl),
    }).setAlpha(0);
    this.tweens.add({ targets: overlay, alpha: 1, duration: 360 });
  }

  private showFeedback(message: string, color: string) {
    this.feedbackText.setText(message).setColor(color).setAlpha(1).setScale(0.92);
    this.tweens.add({ targets: this.feedbackText, scale: 1, alpha: { from: 1, to: 0 }, hold: 900, duration: 300 });
  }

  private recycleBalloon(actor: BalloonActor) {
    actor.root.destroy();
    this.balloons = this.balloons.filter((item) => item !== actor);
  }

  private clearBalloons(except?: BalloonActor) {
    for (const balloon of this.balloons) if (balloon !== except) balloon.root.destroy();
    this.balloons = except ? [except] : [];
  }

  getSnapshot(): TrainAdventureSnapshot {
    const question = this.activeQuestion;
    const level = this.levels[this.levelProgress.levelIndex];
    const snapshotPhase = this.levelProgress.phase === 'level-complete' ||
      (this.levelProgress.phase === 'playing' && this.isTransitioning)
      ? 'transitioning'
      : this.levelProgress.phase;
    return {
      phase: snapshotPhase,
      level: level.id,
      levelQuestionCount: level.questions.length,
      activeQuestionNumber: this.levelProgress.questionIndex + 1,
      activeQuestion: question.prompt,
      expectedAnswer: question.choices.find((choice) => choice.correct)?.label || 'Görsel seçenek',
      score: this.progress.score,
      lives: this.progress.lives,
      streak: this.progress.streak,
      trainOffsetX: Math.round(this.train?.x ?? 0),
      wagons: getWagonRenderStates(this.levelProgress, this.levels).map((wagon) => ({
        questionNumber: wagon.questionIndex + 1,
        prompt: wagon.question.prompt,
        status: wagon.status,
      })),
      balloons: this.balloons.filter((balloon) => !balloon.resolved).map((balloon) => ({
        label: balloon.choice.label,
        x: Math.round(balloon.root.x),
        y: Math.round(balloon.root.y),
        falling: balloon.falling,
      })),
    };
  }

  advanceForTest(milliseconds: number) {
    let remaining = Math.max(0, milliseconds);
    while (remaining > 0) {
      const step = Math.min(16, remaining);
      this.simulate(step);
      remaining -= step;
    }
  }

  private installDebugHooks() {
    window.render_game_to_text = () => JSON.stringify(this.getSnapshot());
    window.advanceTime = (milliseconds: number) => this.advanceForTest(milliseconds);
  }
}
